import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IAuthService } from '@adminprodigydesign/axis';
import {
  finalize,
  firstValueFrom,
  mergeMap,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ActivationPayload {
  action: string;
  email: string;
  otc: string;
  password?: string;
}

export interface ActivationResponse {
  status: 'success' | 'failed';
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService {
  constructor(private http: HttpClient) {}
  private url = `${environment.adminAuth}`;

  async getUser(): Promise<{
    name: string;
    email: string;
    clientName: string;
  } | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ name: string; email: string; clientName: string }>(
          this.url,
        ),
      );
      return response ?? null;
    } catch (error) {
      console.error('Error fetching user data', error);
      return null;
    }
  }

  private logoutUrl = environment.apiBaseUrl;

  logout(): void {
    // The payload to notify the backend to log out the current session.
    const payload = { action: 'logout' };

    // Make an API call to tell the server to destroy the session on its end.
    this.http
      .post(this.logoutUrl, payload)
      .pipe(
        // You may use finalize to ensure cleanup runs whether the call succeeds or fails.
        finalize(() => {
          // Remove local storage items
          localStorage.removeItem('admin-token');
          // Optional: clear other storage values if needed.

          // Redirect to the login page.
          window.location.href = '/login';
        }),
      )
      .subscribe({
        next: (response) => {},
        error: (error) => {
          console.error('Logout API call failed:', error);
          // Even if the call fails, we clear the client state and redirect.
        },
      });
  }

  /**
   * Activates a new user and sets their password.
   * Throws an error with backend message if activation fails.
   */ //TODO: MIGHT NOT NEED ON FRONTEND VERSION
  activateNewUserWithPassword(
    action: 'validNewUserWithPass',
    email: string,
    otc: string,
    password: string,
  ): Observable<void> {
    const payload: ActivationPayload = { action, email, otc, password };
    return this.http
      .post<ActivationResponse>(environment.apiBaseUrl, payload)
      .pipe(
        mergeMap((res) => {
          if (res.status === 'failed') {
            return throwError(
              () => new Error(res.message || 'Activation failed'),
            );
          }
          return of(undefined);
        }),
      );
  }

  /**
   * Activates a new user (password already set).
   * Throws an error with backend message if activation fails.
   */
  activateNewUser(
    action: 'validNewUser',
    email: string,
    otc: string,
  ): Observable<void> {
    const payload: ActivationPayload = { action, email, otc };
    return this.http
      .post<ActivationResponse>(environment.apiBaseUrl, payload)
      .pipe(
        mergeMap((res) => {
          if (res.status === 'failed') {
            return throwError(
              () => new Error(res.message || 'Activation failed'),
            );
          }
          return of(undefined);
        }),
      );
  }
}
